let bootstrapPromise = null;

function getBootstrapEndpoint() {
  return process.env.REACT_APP_FUNNEL_BOOTSTRAP_ENDPOINT || '';
}

function getSessionInviteEndpoint() {
  if (process.env.REACT_APP_FUNNEL_SESSION_INVITE_ENDPOINT) {
    return process.env.REACT_APP_FUNNEL_SESSION_INVITE_ENDPOINT;
  }

  const bootstrapEndpoint = getBootstrapEndpoint();
  return bootstrapEndpoint ? bootstrapEndpoint.replace(/\/bootstrap\/?$/, '/telegram/session-invite') : '';
}

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function unresolvedDestination(reason) {
  return { url: '', resolution: 'unavailable', reason };
}

function isTelegramUrl(url) {
  return typeof url === 'string' && url.trim().startsWith('https://t.me/');
}

function isResolvedTelegramDestination(destination) {
  const url = typeof destination?.url === 'string' ? destination.url.trim() : '';
  const knownResolution = ['z_source', 'z_ad', 'bootstrap_default', 'fallback', 'session_invite'].includes(
    destination?.resolution
  );

  return destination && isTelegramUrl(url) && knownResolution;
}

async function fetchBootstrap(endpoint) {
  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
    credentials: 'omit',
  });

  if (!response.ok) {
    throw new Error(`Bootstrap request failed with ${response.status}`);
  }

  const body = await response.json();
  return isObject(body) && isObject(body.data) ? body.data : null;
}

export async function loadFunnelBootstrap() {
  const endpoint = getBootstrapEndpoint();
  if (!endpoint) {
    throw new Error('REACT_APP_FUNNEL_BOOTSTRAP_ENDPOINT is not configured');
  }

  if (!bootstrapPromise) {
    bootstrapPromise = fetchBootstrap(endpoint).catch((error) => {
      bootstrapPromise = null;
      throw error;
    });
  }

  return bootstrapPromise;
}

export function resolveTelegramDestinationFromBootstrap({
  bootstrap,
  zSource,
  zAd,
  fallbackUrl,
}) {
  const telegram = isObject(bootstrap?.telegram) ? bootstrap.telegram : {};
  const byZSource = isObject(telegram.by_z_source) ? telegram.by_z_source : {};
  const byZAd = isObject(telegram.by_z_ad) ? telegram.by_z_ad : {};
  const sourceUrl = zSource ? byZSource[zSource] : '';
  const adUrl = zAd ? byZAd[zAd] : '';
  const defaultUrl = typeof telegram.default_url === 'string' ? telegram.default_url : '';

  if (typeof sourceUrl === 'string' && sourceUrl) {
    return { url: sourceUrl, resolution: 'z_source' };
  }
  if (typeof adUrl === 'string' && adUrl) {
    return { url: adUrl, resolution: 'z_ad' };
  }
  if (defaultUrl) {
    return { url: defaultUrl, resolution: 'bootstrap_default' };
  }

  return { url: fallbackUrl, resolution: 'fallback' };
}

export async function resolveTelegramDestination({ zSource, zAd, fallbackUrl }) {
  try {
    const bootstrap = await loadFunnelBootstrap();
    const destination = resolveTelegramDestinationFromBootstrap({
      bootstrap,
      zSource,
      zAd,
      fallbackUrl,
    });

    if (isResolvedTelegramDestination(destination)) {
      return destination;
    }

    return unresolvedDestination('campaign_invite_not_found');
  } catch (error) {
    if (isTelegramUrl(fallbackUrl)) {
      return { url: fallbackUrl, resolution: 'fallback', reason: 'bootstrap_unavailable' };
    }

    return unresolvedDestination('bootstrap_unavailable');
  }
}

export async function createTelegramSessionInvite({ context, payload = {} }) {
  const endpoint = getSessionInviteEndpoint();
  if (!endpoint) {
    throw new Error('Telegram session invite endpoint is not configured');
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'omit',
    body: JSON.stringify({
      visitor_session_id: context.visitor_session_id,
      anonymous_user_id: context.anonymous_user_id,
      page_instance_id: context.page_instance_id,
      funnel_variant: context.funnel_variant,
      z_source: context.z_source,
      z_campaign: context.z_campaign,
      z_ad: context.z_ad,
      route_path: context.route_path,
      page_url: context.page_url,
      referrer: context.referrer,
      user_agent: context.user_agent,
      meta: context.meta,
      experiment_key: context.experiment_key,
      experiment_variant: context.experiment_variant,
      assigned_visual_funnel: context.assigned_visual_funnel,
      assignment_id: context.assignment_id,
      assignment_source: context.assignment_source,
      assignment_created_at: context.assignment_created_at,
      payload,
    }),
  });

  if (!response.ok) {
    throw new Error(`Telegram session invite request failed with ${response.status}`);
  }

  const body = await response.json();
  const data = isObject(body) && isObject(body.data) ? body.data : {};
  const inviteLink = typeof data.invite_link === 'string' ? data.invite_link.trim() : '';
  if (!isTelegramUrl(inviteLink)) {
    throw new Error('Telegram session invite response did not include a valid invite link');
  }

  return {
    url: inviteLink,
    resolution: 'session_invite',
    session_invite_id: typeof data.session_invite_id === 'string' ? data.session_invite_id : '',
    invite_code: typeof data.invite_code === 'string' ? data.invite_code : '',
    expires_at: typeof data.expires_at === 'string' ? data.expires_at : '',
    reused: data.reused === true,
  };
}
